// @flow
import { cloneDeep } from 'lodash';

type ValidationState = 'untouched' | 'touched' | 'ok' | 'error';

export type ValidationResult = {
  isValid: boolean,
  name: string,
};

type ValidatorFunction = (value: string, state: FormValue) => ValidationResult;
type FormatterFunction = (value: string, state: FormValue) => string;
type Manipulator = ValidatorFunction | FormatterFunction;

type FieldDef = {
  type: 'field',
  manipulators: Array<Manipulator>,
};

type FormDef = {
  type: 'form',
  fields: {
    [name: string]: DefTypes,
  },
};

type DefTypes = FieldDef | FormDef;

type FieldValue = {
  +type: 'field',
  +value: string,
  +state: ValidationState,
  +isValid: boolean,
  +errorType: string,
};

export type FormValue = {
  +type: 'form',
  +value: {
    [name: string]: ValueTypes,
  },
  +state: ValidationState,
  +isValid: boolean,
  getIn: (key: KeyType) => FieldValue,
  getFormIn: (key: KeyType) => FormValue,
};

type ValueTypes = FieldValue | FormValue;

export function Subform(fields: { [name: string]: DefTypes }): FormDef {
  return {
    type: 'form',
    fields,
  };
}

export function Field(...manipulators: Manipulator[]): FieldDef {
  return {
    type: 'field',
    manipulators,
  };
}

function createInitialState(
  form: FormDef,
  previousFormValue?: FormValue
): FormValue {
  const fields = Object.keys(form.fields).reduce((newValue, current) => {
    const field = form.fields[current];
    if (field.type === 'field') {
      if (previousFormValue && previousFormValue.value[current]) {
        newValue[current] = previousFormValue.value[current];
      } else {
        newValue[current] = {
          type: 'field',
          value: '',
          state: 'untouched',
          isValid: true,
        };
      }
    } else {
      if (
        previousFormValue &&
        previousFormValue.value[current] &&
        previousFormValue.value[current].type === 'form'
      ) {
        newValue[current] = createInitialState(
          field,
          previousFormValue.value[current]
        );
      } else {
        newValue[current] = createInitialState(field);
      }
    }
    return newValue;
  }, {});

  return {
    type: 'form',
    value: fields,
    state: previousFormValue ? 'touched' : 'untouched',
    getIn,
    getFormIn,
    isValid: true,
  };
}

type KeyType = string | Array<string>;

function validateField(
  manipulators: Array<Manipulator>,
  value: string,
  previousFormValue: FormValue,
  formatOnly: boolean = false
) {
  return manipulators.reduce(
    (previous, manipulator) => {
      const result = manipulator(previous.value, previousFormValue);
      if (typeof result === 'string') {
        previous.value = result;
      } else if (!formatOnly) {
        previous.state = result.isValid ? previous.state : 'error';
        previous.isValid = result.isValid ? previous.isValid : false;
        previous.errorType = result.isValid ? previous.errorType : result.name;
      } else {
        previous.state = 'touched';
      }
      return previous;
    },
    {
      type: 'field',
      value,
      state: formatOnly ? 'touched' : 'ok',
      errorType: '',
      isValid: true,
    }
  );
}

function updateField(
  form: FormDef,
  previous: FormValue,
  key: KeyType,
  value: string
): FormValue {
  const name = Array.isArray(key) ? key[0] : key;
  const updatedValue = { ...cloneDeep(previous), state: 'touched' };

  const field = form.fields[name];

  if (field.type === 'field') {
    updatedValue.value[name] = validateField(
      field.manipulators,
      value,
      previous,
      true
    );
  } else if (Array.isArray(key) && previous.value[name].type === 'form') {
    const [, ...remaining] = key;
    updatedValue.value[name] = updateField(
      field,
      previous.value[name],
      remaining,
      value
    );
  }

  return updatedValue;
}

function validateForm(form: FormDef, previous: FormValue, isSubmit): FormValue {
  let state = 'ok';
  let isValid = true;

  const fields = Object.keys(form.fields).reduce((newValue, current) => {
    const field = form.fields[current];
    const previousValue = previous.value[current];

    if (field.type === 'field') {
      if (previous.value[current].state === 'untouched' && !isSubmit) {
        newValue[current] = previous.value[current];
      } else {
        if (previousValue.type === 'field') {
          newValue[current] = validateField(
            field.manipulators,
            previousValue.value,
            previous
          );
        } else {
          throw new Error('Previous state did not contain a field.');
        }
      }
    } else {
      if (previousValue.type === 'form') {
        newValue[current] = validateForm(field, previousValue, isSubmit);
      } else {
        throw new Error('Previous state did not contain a form.');
      }
    }

    state = newValue[current].state === 'error' ? 'error' : state;
    isValid = newValue[current].state === 'error' ? false : isValid;

    return newValue;
  }, {});

  return {
    type: 'form',
    value: fields,
    state,
    getIn,
    getFormIn,
    isValid,
  };
}

function getValue(previous: FormValue, key: KeyType): ValueTypes {
  const name = Array.isArray(key) ? key[0] : key;

  const field = previous.value[name];
  if (field.type === 'field' || !Array.isArray(key)) {
    return field;
  } else {
    const [, ...remaining] = key;
    return getValue(field, remaining);
  }
}

function getIn(key: KeyType): FieldValue {
  const field = getValue(this, key);

  if (field.type === 'field') {
    return field;
  }

  throw new Error('Found value is not of type: field.');
}

function getFormIn(key: KeyType): FormValue {
  const form = getValue(this, key);

  if (form.type === 'form') {
    return form;
  }

  throw new Error('Found value is not of type: form.');
}

export function Form(
  fields: { [name: string]: DefTypes },
  previousState?: FormValue
) {
  const form = {
    type: 'form',
    fields,
  };

  const initialFormValue = createInitialState(form, previousState);

  return {
    initialState: initialFormValue,
    onFieldChange: (
      previous: FormValue,
      key: KeyType,
      value: string
    ): FormValue => updateField(form, previous, key, value),
    onFieldBlur: (previous: FormValue, key: KeyType) =>
      validateForm(form, previous, false),
    onSubmit: (previous: FormValue): FormValue =>
      validateForm(form, previous, true),
  };
}
